import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { TextField } from '@mui/material';
import styled from 'styled-components';
import type { ColumnDef } from '@tanstack/react-table';
import { Flex } from '@/layout';
import { Button } from '@/components/UI/Button';
import PageContainer from '@/components/Container/PageContainer';
import { getPublicShareContents, getPublicShareInfo, verifySharePassword } from '@/api/document.api';
import { Documents, Download, Folder } from '@/components/Icon';
import { Text } from '@/components/UI/Typography/Text';
import DataTable, { TypeColumn } from '@/components/DataTable';
import { convertToData, convertToFileSize } from '@/utils/convert';
import { theme } from '@/theme/Theme';
import Loading from '@/components/UI/Loading';
import { openStorageDownload } from '@/utils/storageDownload';
import useCurrentLang from '@/hooks/useCurrentLang';
import { publicShareCopy } from './copy';
import { publicShareFailure, savePublicShareZip } from './download';

type DataRow = FolderOrFileItem & { action: string; added: boolean; id?: string };
const ShareSurface = styled.div`
  min-width: 0;
  font-size: ${(p) => p.theme.fontSizes.base};
  .folder-link {
    display: inline-flex;
    align-items: center;
    gap: 10px;
    padding: 8px 0;
    min-height: 44px;
    max-width: 100%;
    border: 0;
    background: transparent;
    color: ${(p) => p.theme.colors.primary500};
    font: inherit;
    text-align: left;
    text-decoration: underline;
    cursor: pointer;
    overflow-wrap: anywhere;
  }
  .folder-link:hover {
    text-decoration-thickness: 2px;
  }
  button:focus-visible {
    outline: 3px solid #0b319d;
    outline-offset: 3px;
  }
  button {
    min-height: 44px;
    min-width: 0;
    height: auto;
    max-width: 100%;
    line-height: 1.5;
    padding-top: 8px;
    padding-bottom: 8px;
    white-space: normal;
  }
  .share-actions {
    margin: 16px 0;
  }
  .share-error {
    color: ${(p) => p.theme.colors.error};
  }
  p {
    line-height: 1.6;
    overflow-wrap: anywhere;
  }
`;

export default function Share() {
  const { t } = useTranslation();
  const lang = useCurrentLang();
  const c = publicShareCopy(lang === 'en');
  const { code } = useParams<{ code: string }>();
  const activeCode = useRef(code);
  activeCode.current = code;
  const requestId = useRef(0);
  const zipLock = useRef(false);
  const tabLabel = t('pages.dealDetail.manageFiles.myFolder.table.columns', { returnObjects: true });
  const [isLoading, setIsLoading] = useState(false);
  const [data, setData] = useState<DataRow[]>([]);
  const [currentFolderId, setCurrentFolderId] = useState<number | null>(null);
  const [breadcrumbs, setBreadcrumbs] = useState<{ id: number; name: string }[]>([]);
  const [rootTarget, setRootTarget] = useState<{ id: number; name: string; folder: boolean } | null>(null);
  const [requiresPassword, setRequiresPassword] = useState(false);
  const [sharePassword, setSharePassword] = useState('');
  const [sessionToken, setSessionToken] = useState<string | null>(null);
  const [error, setError] = useState('');
  const [downloadError, setDownloadError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [zipFolder, setZipFolder] = useState<number | null>(null);

  const handleFailure = useCallback(
    async (failure: unknown, downloading = false) => {
      const result = await publicShareFailure(failure);
      if (requiresPassword && [401, 403].includes(result.status || 0)) {
        setSessionToken(null);
        setSharePassword('');
        setData([]);
        setPasswordError(c.sessionExpired);
      }
      if (downloading) setDownloadError(c.downloadFailed);
      else setError(c.failed);
    },
    [requiresPassword, c.failed, c.downloadFailed, c.sessionExpired]
  );

  const loadContents = useCallback(
    async (folderId: number, token: string | null, name?: string) => {
      if (!code) return;
      const request = ++requestId.current;
      setIsLoading(true);
      setError('');
      setDownloadError('');
      try {
        const response = await getPublicShareContents(code, {
          folder_id: folderId,
          share_session_token: token || undefined,
        });
        if (request !== requestId.current || activeCode.current !== code) return;
        setData(
          response.data.data.map(
            (item: FolderOrFileItem): DataRow => ({
              ...item,
              id: String(item.id),
              size: convertToFileSize(Number(item.size)),
              updated_at: convertToData(item.updated_at),
              action: item.path,
              added: false,
            })
          )
        );
        setCurrentFolderId(folderId);
        if (name)
          setBreadcrumbs((previous) => {
            const index = previous.findIndex((item) => item.id === folderId);
            return index >= 0 ? previous.slice(0, index + 1) : [...previous, { id: folderId, name }];
          });
      } catch (failure) {
        if (request !== requestId.current || activeCode.current !== code) return;
        setData([]);
        await handleFailure(failure);
      } finally {
        if (request === requestId.current && activeCode.current === code) setIsLoading(false);
      }
    },
    [code, handleFailure]
  );

  const initialize = useCallback(async () => {
    if (!code) return;
    const request = ++requestId.current;
    setIsLoading(true);
    setError('');
    try {
      const response = await getPublicShareInfo(code);
      if (request !== requestId.current || activeCode.current !== code) return;
      const info = response.data.data;
      const root = { id: Number(info.target.id), name: info.target_name, folder: info.target_type === 'folder' };
      setRootTarget(root);
      setRequiresPassword(Boolean(info.requires_password));
      setBreadcrumbs(root.folder ? [{ id: root.id, name: root.name }] : []);
      setCurrentFolderId(root.id);
      if (!info.requires_password) await loadContents(root.id, null);
    } catch {
      if (activeCode.current === code) setError(c.failed);
    } finally {
      if (activeCode.current === code) setIsLoading(false);
    }
  }, [code, loadContents, c.failed]);

  useEffect(() => {
    setSessionToken(null);
    setSharePassword('');
    setPasswordError('');
    setData([]);
    setRootTarget(null);
    setBreadcrumbs([]);
    setError('');
    setDownloadError('');
    void initialize();
    return () => {
      ++requestId.current;
    };
    // A different share must not inherit a verified session. Password state is handled by unlockShare.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [code]);

  const unlockShare = async () => {
    if (!code || !rootTarget || !sharePassword) return;
    setIsLoading(true);
    setPasswordError('');
    setError('');
    try {
      const response = await verifySharePassword(code, sharePassword);
      if (activeCode.current !== code) return;
      const token = response.data.data.share_session_token;
      setSessionToken(token);
      setSharePassword('');
      await loadContents(rootTarget.id, token);
    } catch {
      if (activeCode.current === code) setPasswordError(c.passwordError);
    } finally {
      if (activeCode.current === code) setIsLoading(false);
    }
  };
  const downloadZip = async (folderId: number, name: string) => {
    if (!code || zipLock.current) return;
    zipLock.current = true;
    setZipFolder(folderId);
    setDownloadError('');
    try {
      await savePublicShareZip(code, name, folderId, sessionToken || undefined);
    } catch (failure) {
      if (activeCode.current === code) await handleFailure(failure, true);
    } finally {
      zipLock.current = false;
      setZipFolder(null);
    }
  };
  const downloadFile = async (path: string) => {
    setDownloadError('');
    try {
      await openStorageDownload(path, { shareSessionToken: sessionToken || undefined });
    } catch (failure) {
      await handleFailure(failure, true);
    }
  };

  const columns: ColumnDef<DataRow>[] = useMemo(
    () => [
      {
        accessorKey: 'name',
        header: tabLabel[0],
        cell: ({ row }) =>
          row.original.type === 'folder' ? (
            <button
              type="button"
              className="folder-link"
              disabled={isLoading}
              onClick={() => void loadContents(Number(row.original.id), sessionToken, row.original.name)}
            >
              <span aria-hidden="true">{row.original.name === '..' ? <Folder.ReturnSVG /> : <Folder.FolderSVG />}</span>
              {row.original.name}
            </button>
          ) : (
            <Flex gridGap="10px">
              <span aria-hidden="true">
                <Documents.DocumentsSVG fill={theme.colors.primary500} />
              </span>
              <Text>{row.original.name}</Text>
            </Flex>
          ),
      },
      {
        accessorKey: 'type',
        header: tabLabel[1],
        cell: ({ row }) => (row.original.type === 'folder' ? '-' : <TypeColumn text={row.original.type} />),
      },
      {
        accessorKey: 'size',
        header: tabLabel[2],
        cell: ({ row }) => (row.original.type === 'folder' ? '-' : row.original.size),
      },
      { accessorKey: 'updated_at', header: tabLabel[3] },
      {
        accessorKey: 'action',
        header: tabLabel[4],
        cell: ({ row }) =>
          row.original.name === '..' ? null : row.original.type === 'folder' ? (
            <Button
              type="button"
              size="sm"
              variant="primary-invert"
              disabled={zipFolder !== null || isLoading}
              aria-label={`${c.zip}: ${row.original.name}`}
              onClick={() => void downloadZip(Number(row.original.id), row.original.name)}
            >
              {zipFolder === Number(row.original.id) ? c.preparing : c.zip}
            </Button>
          ) : (
            <Button
              type="button"
              size="sm"
              variant="primary-invert"
              aria-label={`${c.download}: ${row.original.name}`}
              onClick={() => void downloadFile(row.original.path)}
            >
              <Download.DownloadSVG aria-hidden="true" />
            </Button>
          ),
      },
    ],
    [
      tabLabel,
      isLoading,
      sessionToken,
      loadContents,
      zipFolder,
      c.zip,
      c.preparing,
      c.download,
      downloadZip,
      downloadFile,
    ]
  );

  const unlocked = !requiresPassword || Boolean(sessionToken);
  return (
    <PageContainer title={c.title}>
      <ShareSurface>
        {requiresPassword && !sessionToken && (
          <form
            onSubmit={(event) => {
              event.preventDefault();
              void unlockShare();
            }}
          >
            <Flex alignItems="center" gridGap="12px" flexWrap="wrap">
              <TextField
                type="password"
                name="share-password"
                autoComplete="off"
                label={c.password}
                value={sharePassword}
                onChange={(event) => setSharePassword(event.target.value)}
                error={Boolean(passwordError)}
              />
              <Button type="submit" variant="primary" disabled={isLoading || !sharePassword}>
                {c.unlock}
              </Button>
            </Flex>
            {passwordError && (
              <p role="alert" className="share-error">
                {passwordError}
              </p>
            )}
          </form>
        )}
        {error && (
          <div role="alert">
            <p className="share-error">{error}</p>
            <Button
              type="button"
              variant="primary-invert"
              disabled={isLoading}
              onClick={() =>
                void (rootTarget && unlocked
                  ? loadContents(currentFolderId || rootTarget.id, sessionToken)
                  : initialize())
              }
            >
              {c.retry}
            </Button>
          </div>
        )}
        {unlocked && rootTarget && (
          <>
            <nav aria-label={lang === 'en' ? 'Shared folder path' : '分享資料夾路徑'}>
              <Flex alignItems="center" gridGap="10px" flexWrap="wrap">
                {breadcrumbs.map((item, index) => (
                  <Flex key={item.id} alignItems="center" gridGap="5px">
                    {index > 0 && <span aria-hidden="true">/</span>}
                    <button
                      type="button"
                      className="folder-link"
                      aria-current={index === breadcrumbs.length - 1 ? 'location' : undefined}
                      disabled={isLoading}
                      onClick={() => void loadContents(item.id, sessionToken, item.name)}
                    >
                      <span aria-hidden="true">
                        <Folder.FolderSVG />
                      </span>
                      {item.name}
                    </button>
                  </Flex>
                ))}
              </Flex>
            </nav>
            {rootTarget.folder && (
              <div className="share-actions">
                <Button
                  type="button"
                  variant="primary"
                  disabled={zipFolder !== null || isLoading}
                  onClick={() =>
                    void downloadZip(
                      currentFolderId || rootTarget.id,
                      breadcrumbs[breadcrumbs.length - 1]?.name || rootTarget.name
                    )
                  }
                >
                  {zipFolder === currentFolderId ? c.preparing : c.zip}
                </Button>
                <p>{c.limits}</p>
              </div>
            )}
            {downloadError && (
              <p role="alert" className="share-error">
                {downloadError}
              </p>
            )}
            <div aria-live="polite">{zipFolder !== null && <p role="status">{c.preparing}</p>}</div>
            {isLoading ? (
              <Loading />
            ) : (
              !error && (data.length ? <DataTable columns={columns} data={data} /> : <p>{c.empty}</p>)
            )}
          </>
        )}
        {isLoading && !rootTarget && <Loading />}
      </ShareSurface>
    </PageContainer>
  );
}
